import React, {useCallback, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Formik, Form, useFormikContext} from 'formik'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import {useRails} from '~/react/contexts/rails'
import {onJsonResponse} from '~/react/hooks/jsonResult'
import Field from '~/react/components/FormField'
import {EmailIcon} from '~/react/icons'
import useUpdatePayee from './useUpdatePayee'
import useAbnVerification from './useAbnVerification'
import AbnVerificationField from './AbnVerificationField'
import {validationSchema} from './schema'

// Inner component so we can access Formik context to set field errors from outside the form
const AbnVerificationEffect = ({verificationStatus}) => {
  const {t} = useTranslation('pointsOnPayments')
  const {setFieldError, setFieldTouched} = useFormikContext()

  useEffect(() => {
    if (verificationStatus === 'invalid') {
      setFieldTouched('businessNumber', true, false)
      setFieldError(
        'businessNumber',
        t('pointsOnPayments:updatePayeeModal.fields.businessNumber.verificationFailedError'),
      )
    } else if (verificationStatus === 'valid') {
      setFieldError('businessNumber', undefined)
    }
  }, [verificationStatus, setFieldError, setFieldTouched, t])

  return null
}

AbnVerificationEffect.propTypes = {
  verificationStatus: PropTypes.oneOf(['idle', 'loading', 'valid', 'invalid']).isRequired,
}

const UpdatePayeeModal = ({isOpen, onClose, payee, onPayeeUpdated}) => {
  const {t} = useTranslation('pointsOnPayments')
  const {currentSupplier, authenticityToken} = useRails()
  const {updatePayee, isUpdating} = useUpdatePayee(currentSupplier.id, payee.id)
  const {
    status: verificationStatus,
    entityName,
    verifiedAbnRef,
    triggerVerification,
    resetVerification,
  } = useAbnVerification({supplierId: currentSupplier.id, authenticityToken})

  const initialValues = useMemo(
    () => ({
      name: payee.name || '',
      email: payee.email || '',
      businessNumber: payee.businessNumber || '',
      bankAccount: {
        name: payee.bankAccount?.name || '',
        bsb: payee.bankAccount?.bsb || '',
        accountNumber: payee.bankAccount?.accountNumber || '',
      },
      lockVersion: payee.lockVersion,
    }),
    [payee],
  )

  const handleAbnChange = useCallback(
    (newAbn) => {
      if (verifiedAbnRef.current !== null && newAbn !== verifiedAbnRef.current) {
        resetVerification()
      }
    },
    [resetVerification, verifiedAbnRef],
  )

  const handleSubmit = async (values, {setFieldError}) => {
    const result = await updatePayee(values)

    const onSuccess = (showMessage) => {
      onPayeeUpdated(result.payee)
      showMessage(t('pointsOnPayments:updatePayeeModal.toast.success'))
      onClose()
    }

    const onFailure = (key, error, value) =>
      setFieldError(
        key,
        t(
          [
            `pointsOnPayments:updatePayeeModal.fields.${key}.${error}Error`,
            `pointsOnPayments:updatePayeeModal.fields.${key}.invalidError`,
          ],
          {value},
        ),
      )

    onJsonResponse(result, {onSuccess, onFailure})
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      scrollBehavior="inside"
      preserveScrollBarGap
    >
      <ModalOverlay zIndex="modal" />
      <Formik
        initialValues={initialValues}
        validateOnMount
        validationSchema={validationSchema(t)}
        onSubmit={handleSubmit}
      >
        {({isSubmitting, isValid, dirty, values}) => {
          const needsVerification =
            values.businessNumber !== '' &&
            values.businessNumber !== initialValues.businessNumber &&
            verificationStatus !== 'valid'

          return (
            <ModalContent as={Form}>
              <AbnVerificationEffect verificationStatus={verificationStatus} />
              <ModalCloseButton />
              <ModalHeader borderBottom="1px solid" borderColor="mld.neutral.200">
                <Text textStyle="heading-h4">
                  {t('pointsOnPayments:updatePayeeModal.title')}
                </Text>
                <Text textStyle="body-copy" color="mld.neutral.700" pt="md">
                  {t('pointsOnPayments:updatePayeeModal.subTitle', {
                    accountingProvider: currentSupplier.accountingProvider.name,
                  })}
                </Text>
              </ModalHeader>
              <ModalBody>
                <Field
                  type="text"
                  name="name"
                  label={t('pointsOnPayments:updatePayeeModal.fields.name.label')}
                />
                <Field
                  type="email"
                  name="email"
                  label={t('pointsOnPayments:updatePayeeModal.fields.email.label')}
                  icon={<EmailIcon />}
                />
                <AbnVerificationField
                  label={t('pointsOnPayments:updatePayeeModal.fields.businessNumber.label')}
                  initialAbn={initialValues.businessNumber}
                  verificationStatus={verificationStatus}
                  entityName={entityName}
                  onVerifyClick={triggerVerification}
                  onAbnChange={handleAbnChange}
                  verifyButtonLabel={t(
                    'pointsOnPayments:updatePayeeModal.fields.businessNumber.verifyButton',
                  )}
                  retryButtonLabel={t(
                    'pointsOnPayments:updatePayeeModal.fields.businessNumber.retryButton',
                  )}
                />
                <Field
                  type="text"
                  name="bankAccount.name"
                  label={t(
                    'pointsOnPayments:updatePayeeModal.fields.bankAccountName.label',
                  )}
                />
                <Field
                  type="text"
                  name="bankAccount.bsb"
                  label={t(
                    'pointsOnPayments:updatePayeeModal.fields.branchCode.label',
                  )}
                />
                <Field
                  type="text"
                  name="bankAccount.accountNumber"
                  label={t(
                    'pointsOnPayments:updatePayeeModal.fields.accountNumber.label',
                  )}
                />
              </ModalBody>
              <ModalFooter>
                <Button
                  type="submit"
                  variant="primary"
                  colorScheme="primary"
                  isLoading={isSubmitting || isUpdating}
                  isDisabled={
                    isSubmitting || isUpdating || !isValid || !dirty || needsVerification
                  }
                  mr="sm"
                >
                  {t('pointsOnPayments:updatePayeeModal.buttons.save')}
                </Button>
                <Button variant="secondary" onClick={onClose}>
                  {t('pointsOnPayments:updatePayeeModal.buttons.cancel')}
                </Button>
              </ModalFooter>
            </ModalContent>
          )
        }}
      </Formik>
    </Modal>
  )
}

UpdatePayeeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  payee: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    email: PropTypes.string,
    businessNumber: PropTypes.string,
    bankAccount: PropTypes.shape({
      name: PropTypes.string,
      bsb: PropTypes.string,
      accountNumber: PropTypes.string,
    }),
    lockVersion: PropTypes.number,
  }).isRequired,
  onPayeeUpdated: PropTypes.func.isRequired,
}

export default UpdatePayeeModal
